import { readEnvVariable } from '../utils';
import { addQueryParamsToUrl } from '../utils/common';
import { NewRelicLog } from '../utils/types';

// B1: batch logs to New Relic instead of one fetch per line. The NR Log API
// accepts an array of log objects per request, so we accumulate lines and flush
// them in a single request on size threshold, on an interval, and when the tab
// is being hidden/unloaded. This collapses the previous one-POST-per-log volume.
const MAX_BATCH_SIZE = 50;
const FLUSH_INTERVAL_MS = 5000;

let queue: NewRelicLog[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const getNewRelicUrl = () => {
  const newRelicLogAPI = readEnvVariable('NEW_RELIC_LOG_API');
  const NR_LOG_API_LICENSE_KEY = readEnvVariable(
    'NEW_RELIC_LOG_API_LICENSE_KEY'
  );
  return addQueryParamsToUrl(newRelicLogAPI, {
    'Api-Key': NR_LOG_API_LICENSE_KEY,
  });
};

const clearFlushTimer = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
};

// Send everything currently queued in a single request. On page-hide we use
// sendBeacon, since an ordinary async fetch is cancelled by the unload; the API
// key travels as a URL query param so no custom headers are needed there.
const flush = (useBeacon = false) => {
  clearFlushTimer();
  if (!queue.length) {
    return;
  }
  const batch = queue;
  queue = [];

  // Logging must never throw into the caller (postLogToNewRelic runs on the
  // app's call stack inside the logger), so the entire send path — including
  // URL/body construction — is guarded.
  try {
    const url = getNewRelicUrl();
    const body = JSON.stringify(batch);

    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const sent = navigator.sendBeacon(
        url,
        new Blob([body], { type: 'application/json' })
      );
      if (sent) {
        return;
      }
    }

    // keepalive lets the request complete across a page transition when the
    // beacon path was not used/available.
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: useBeacon,
    }).catch(() => {
      // best-effort: drop this batch on failure (matches prior behaviour of
      // dropping a single failed log).
    });
  } catch {
    // best-effort: drop this batch on failure; never surface to the caller.
  }
};

const scheduleFlush = () => {
  if (queue.length >= MAX_BATCH_SIZE) {
    flush();
    return;
  }
  if (!flushTimer) {
    flushTimer = setTimeout(() => flush(), FLUSH_INTERVAL_MS);
  }
};

// In Jest, keep the original synchronous, un-batched behaviour so no background
// flush timer outlives a test run (which would leave open handles).
const isTestEnv = process.env.NODE_ENV === 'test';

const postSingle = async (logMessage: NewRelicLog) => {
  try {
    await fetch(getNewRelicUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify([logMessage]),
    });
  } catch (error) {
    console.error('Failed to send logs to New Relic.');
  }
};

const postLogToNewRelic = (logMessage: NewRelicLog) => {
  if (isTestEnv) {
    return postSingle(logMessage);
  }
  queue.push(logMessage);
  scheduleFlush();
};

// Flush the tail when the tab is hidden/unloaded so queued lines aren't lost.
// visibilitychange -> hidden is the reliable signal on mobile and for bfcache.
if (!isTestEnv && typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => flush(true));
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush(true);
    }
  });
}

export { postLogToNewRelic };
